import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { tokens, type Types } from '@fiverr-private/theme';
import { ICON_BUTTON_SIZES, type IconButtonProps } from '@fiverr-private/button';
import { type CarouselContextProps, useCarouselContext } from '../Carousel/constants';
import Arrow from './Arrow';
import type { ArrowsProps } from './types';

const calculatePosition = (
  size: IconButtonProps['size'],
  position: ArrowsProps['position'],
  hovered: CarouselContextProps['hovered']
) => {
  if (position === 'default-hover' && size && hovered) {
    return ICON_BUTTON_SIZES[size].width;
  }
  return 0;
};

const getDefaultPositionStyles = (
  isLeft: boolean,
  position: ArrowsProps['position'],
  size: IconButtonProps['size'],
  hovered: CarouselContextProps['hovered']
) =>
  ({
    position: 'absolute',
    margin: 'auto',
    top: 0,
    bottom: 0,
    left: isLeft ? calculatePosition(size, position, hovered) : 'initial',
    right: !isLeft ? calculatePosition(size, position, hovered) : 'initial',
    transform: isLeft ? 'translateX(-50%)' : 'translateX(50%)',
    transition:
      position === 'default-hover' ? `${isLeft ? 'left' : 'right'} ${tokens.animationDuration.moderate1}` : undefined,
    visibility: position === 'default-hover' && !hovered ? 'hidden' : 'visible',
    marginRight: isLeft ? '0.5' : '0',
    marginLeft: !isLeft ? '0.5' : '0',
  } as Types.ContainerProps);

const getTopRightPositionStyles = (isLeft: boolean) =>
  ({
    marginY: '1',
    marginRight: !isLeft ? '1' : 0,
    marginLeft: isLeft ? '1' : 0,
  } as Types.ContainerProps);

const Arrows = ({ onArrowClick, size = 'md', position = 'default' }: ArrowsProps) => {
  const { hovered } = useCarouselContext();
  const isDefaultArrows = position === 'default' || position === 'default-hover';
  const getStyles = isDefaultArrows ? getDefaultPositionStyles : getTopRightPositionStyles;
  const arrowSize = isDefaultArrows ? size : 'xs';

  const ArrowsContainer = !isDefaultArrows
    ? ({ children }) => (
        <Stack gap="3" justifyContent="flexEnd">
          {children}
        </Stack>
      )
    : React.Fragment;

  return (
    <ArrowsContainer>
      <Arrow
        arrowDirection="left"
        onArrowClick={onArrowClick}
        size={arrowSize}
        hideWhenDisabled={isDefaultArrows}
        {...getStyles(true, position, arrowSize, hovered)}
      />
      <Arrow
        arrowDirection="right"
        onArrowClick={onArrowClick}
        size={arrowSize}
        hideWhenDisabled={isDefaultArrows}
        {...getStyles(false, position, arrowSize, hovered)}
      />
    </ArrowsContainer>
  );
};

export default Arrows;
